import React, {useState, useEffect, useMemo} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import EntryFormHeader from '../../components/header/EntryFormHeader';
import {RootState} from '../../redux/store';
import {setField} from '../../redux/entryFormSlice';
import { NEW_VERIFICATION_WITH_PARAM } from '../../graphql/queries';
import { SUBMIT_FORM_INPUT } from '../../graphql/mutations';
import Loading from '../../components/common/Loading';

interface LegalPersonalEntryFormEKYCType {
}

declare const Trustdock: any;

const LegalPersonalEntryFormEKYC: React.FC<LegalPersonalEntryFormEKYCType> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const entryFormInfo = useSelector((state: RootState) => state.entryForm);

  useEffect(() => {
    if((entryFormInfo.entryFormStep !== 'ekyc') && (entryFormInfo.entryFormStep !== 'complete')) {
      dispatch(setField({field: 'entryFormStep', value: 'entry'}))
      navigate('/form/legalPersonal')
    }
  }, [entryFormInfo.entryFormStep])

  const {data, loading, error} = useQuery(NEW_VERIFICATION_WITH_PARAM, {
    variables: {
      externalId: entryFormInfo.externalId
    }
  });
  const [submitFormInput] = useMutation(SUBMIT_FORM_INPUT);

  const [trustdock, setTrustdock] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadedTrustdock, setIsLoadedTrustdock] = useState<boolean>(false);

  useEffect(() => {
    if (!data) {
      return;
    }
    console.log("Public ID : ", data.newVerification.publicId);

    if (entryFormInfo.selectedRole == "attorney") console.log("you selected attorney role.")
    const script = document.createElement('script');

    script.src = process.env.REACT_APP_TRUSTDOCK_HELPER_URL || 'https://helper.test.trustdock.io/v2/verification_helper.js';
    script.async = true;
    script.onload = () => {
      if (isLoadedTrustdock) {
        removeTrustdock();
        console.log("Trustdock removed : ");
      }
      const trustdockInstance = new Trustdock({
        publicId: data.newVerification.publicId,
        plans: [
          {
            id: entryFormInfo.planId_SUPPLEMENTARY_DOCUMENT_VERIFICATION_SERVICE_LETTER_OF_ATTORNEY,
          }
        ],
        openerSelector: '.opener',
      });

      console.log("TrustDock Instance : ", trustdockInstance);

      setTrustdock(trustdockInstance);
      if (trustdockInstance.isLoaded) {
        setIsLoadedTrustdock(true);
      } else {
        trustdockInstance.on('loaded', () => {
          console.log("Trustdock is loaded");
          setIsLoadedTrustdock(true);
          
          //// Processing when an event is received
          // Describe the process when identity verification documents have been submitted
          // For example, forcing a transition to the next page for users who do not close the modal after submission, etc.
          trustdockInstance.on('documentsubmitted', documentsubmittedAction)
          // Describes the process when identity verification documents have been submitted and the modal is closed
          trustdockInstance.on('completed', completedAction)
          // Describes what to do if the modal is closed without submitting identification documents
          trustdockInstance.on('canceled', canceledAction)
        });
      }
    }

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    }
  }, [data]);

  const handleClick = () => {
    if (trustdock) {
      trustdock.open();
    }
  }

  const documentsubmittedAction = () => {
    console.log('received the documentsubmitted event');
    setIsLoading(false);
  }
  const canceledAction = () => {
    console.log('received the canceled event');
    setIsLoading(false);
    window.alert('Trustdock Uploader is failed');
  }
  const completedAction = async () => {
    try {
      const submitFormResult = await submitFormInput({
        variables: {
          externalId: entryFormInfo.externalId,
          corporatePublicId: data.newVerification.publicId,
          personalPublicId: null,
          planId: entryFormInfo.planId_SUPPLEMENTARY_DOCUMENT_VERIFICATION_SERVICE_LETTER_OF_ATTORNEY,
          corporateName: entryFormInfo.companyName,
          corporateNumber: entryFormInfo.corporateNumber,
          corporatePostalCode: entryFormInfo.companyPostalCode,
          corporatePrefecture: entryFormInfo.companyPrefecture,
          corporateCity: entryFormInfo.companyCity,
          corporateStreet: entryFormInfo.companyStreetAddress,
          corporateBuilding: entryFormInfo.companyBuilding || '',
          representativeLastName: entryFormInfo.representativeLastName,
          representativeFirstName: entryFormInfo.representativeFirstName,
          personalLastName: entryFormInfo.lastName,
          personalFirstName: entryFormInfo.firstName,
          personalLastNameKana: entryFormInfo.lastNameKa,
          personalFirstNameKana: entryFormInfo.firstNameKa,
          personalBirthday: entryFormInfo.birthday,
          personalPostalCode: entryFormInfo.postalCode,
          personalPrefecture: entryFormInfo.prefecture,
          personalCity: entryFormInfo.city,
          personalStreet: entryFormInfo.address,
          personalBuilding: entryFormInfo.building || '',
          personalGender: entryFormInfo.gender,
          personalPhone: entryFormInfo.phoneNum,
          personalEmail: entryFormInfo.email
        }
      });

      dispatch(setField({field: 'submissionDate', value: submitFormResult.data.submitFormInput}));
      dispatch(setField({field: 'entryFormStep', value: 'complete'}))
      navigate('/form/legalPersonal/complete');
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  }

  const removeTrustdock = () => {
    // Delete registered listeners with `trustdock.off`
    // Note that an anonymous function cannot be specified as a target for deletion, so it cannot be deleted.
    trustdock.off('documentsubmitted', documentsubmittedAction)
    trustdock.off('completed', completedAction)
    trustdock.off('canceled', canceledAction)
    // Delete generated DOM elements
    trustdock.remove()
    // It is possible to delete the file and use it again in another process by `new Trustdock()`.
  }

  if(data) {
    console.log("API Data : ", data);
    dispatch(setField({field: 'publicId', value: data.newVerification.publicId}));
    dispatch(setField({field: 'externalId', value: data.newVerification.externalId}));
    dispatch(setField({field: 'planId_SUPPLEMENTARY_DOCUMENT_VERIFICATION_SERVICE_LETTER_OF_ATTORNEY', value: data.newVerification.plans.find((item: any) => item.name === 'SUPPLEMENTARY_DOCUMENT_VERIFICATION_SERVICE_LETTER_OF_ATTORNEY').id}));
  }

  if (loading) return <Loading />;
  if (error) return <p className="text-center mt-4 text-red-500">Error: {error.message}</p>;

  return (
    <>
      <div className="bg-gray-50">
        <EntryFormHeader currentStep={4}/>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <h1 className="text-2xl font-bold mb-6">委任状のアップロード</h1>
          <p className="text-gray-600 mb-8">
            代表者以外の方が手続きを行う場合は、委任状のアップロードが必要です。
          </p>

          <div className="info-box">
            <h2 className="font-semibold mb-2">アップロード可能なファイル形式と制限</h2>
            <div className="space-y-4">
              <div>
                <p className="font-medium mb-1">画像ファイルの場合</p>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>ファイル形式：JPG、JPEG、PNG</li>
                  <li>ファイルサイズ：10MB以内</li>
                  <li>推奨解像度：1200×1600ピクセル以上</li>
                </ul>
              </div>
              <div>
                <p className="font-medium mb-1">PDFファイルの場合</p>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>ファイルサイズ：20MB以内</li>
                  <li>ページ数：1ページのみ</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="notice-box">
            <h2 className="font-semibold mb-2">注意事項</h2>
            <ul className="space-y-2 text-sm">
              <li>• 委任状は発行から3ヶ月以内のものを提出してください</li>
              <li>• 代表者の押印または署名が必要です</li>
              <li>• 委任内容が不明確な場合は再提出をお願いする場合があります</li>
              <li>• スキャンまたは撮影時は、文字が鮮明に読み取れるようにしてください</li>
              <li>• 影や光の反射がないように注意してください</li>
              <li>• 委任状の四隅が全て写るようにしてください</li>
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
            <button
              type="button"
              id="nextButton"
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => {
                setIsLoading(true);
                if (!isLoading)
                  handleClick();
              }}
            >
              {isLoading ? '読み込み中...' : 'アップロード'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default LegalPersonalEntryFormEKYC;
